import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'
import { API_CONFIG, HTTP_STATUS } from '../constants'
import { getApiBaseUrl } from '../utils/apiUrl'
import { getCsrfTokenFromCookie } from '../utils/cookieUtils'
import { analyticsService } from './analyticsService'
import i18n from '@/i18n'

interface RequestMetadata {
  startTime: Date
}

type CustomAxiosRequestConfig = InternalAxiosRequestConfig & {
  metadata?: RequestMetadata
  _retry?: boolean
  _retryCount?: number
  _csrfRetry?: boolean
  _csrf401Retry?: boolean
  suppressErrorLog?: boolean
  suppressErrorToast?: boolean
}

interface OfflineRequest {
  method?: string
  url?: string
  data?: unknown
  [key: string]: unknown
}

class ApiClient {
  private axios: AxiosInstance
  private retryCount: number
  private offlineQueue: OfflineRequest[]
  private isOnline: boolean
  private csrfToken: string | null
  private csrfInitialized: Promise<void> | null
  private isRefreshing: boolean
  private refreshPromise: Promise<void> | null

  constructor() {
    const baseURL = getApiBaseUrl()

    this.axios = axios.create({
      baseURL: baseURL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    })

    this.retryCount = API_CONFIG.RETRY_ATTEMPTS
    this.offlineQueue = []
    this.isOnline = navigator.onLine
    this.csrfToken = null
    this.csrfInitialized = null
    this.isRefreshing = false
    this.refreshPromise = null

    this.setupInterceptors()
    this.setupOfflineDetection()
    this.csrfInitialized = this.initializeCsrf()
  }

  private setupInterceptors(): void {
    this.axios.interceptors.request.use(
      async (config: CustomAxiosRequestConfig) => {
        if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase() ?? '')) {
          let csrfToken = getCsrfTokenFromCookie()

          if (!csrfToken && !config._csrfRetry) {
            config._csrfRetry = true
            if (process.env.NODE_ENV === 'development') {
              console.log('🔒 CSRF token missing, fetching...')
            }
            await this.initializeCsrf()
            csrfToken = getCsrfTokenFromCookie()
          }

          if (csrfToken) {
            config.headers['X-XSRF-TOKEN'] = csrfToken
          } else if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️  CSRF token still missing after fetch attempt')
          }
        }

        if (process.env.NODE_ENV === 'development') {
          console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, config.data)
        }

        config.headers['Accept-Language'] = i18n.language || 'fi'
        config.metadata = { startTime: new Date() }

        return config
      },
      (error: AxiosError) => {
        return Promise.reject(error)
      }
    )

    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        const config = response.config as CustomAxiosRequestConfig
        const duration = Date.now() - (config.metadata?.startTime?.getTime() ?? Date.now())

        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ ${config.method?.toUpperCase()} ${config.url} (${duration}ms)`)
        }

        if (Math.random() < 0.1) {
          try {
            analyticsService.track('system.api.latency', {
              method: config.method,
              url: config.url,
              duration_ms: duration,
              status_code: response.status,
            })
          } catch {
            // Analytics must never break the app
          }
        }

        return response
      },
      async (error: unknown) => {
        if (!(error instanceof AxiosError)) {
          return Promise.reject(error)
        }

        const originalRequest = error.config as CustomAxiosRequestConfig | undefined

        if (!originalRequest) {
          return Promise.reject(error)
        }

        if (
          error.name === 'AbortError' ||
          error.name === 'CanceledError' ||
          error.code === 'ERR_CANCELED' ||
          this.isCancel(error)
        ) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔄 Request canceled: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`)
          }
          return Promise.reject(error)
        }

        if (process.env.NODE_ENV === 'development' && !originalRequest.suppressErrorLog) {
          console.error(
            `❌ ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`,
            (error.response?.data as Record<string, unknown>) || error.message
          )
        }

        const isAuthRequest = originalRequest.url?.startsWith('/auth/')

        if (
          error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
          !isAuthRequest &&
          !originalRequest._csrf401Retry &&
          ['post', 'put', 'delete', 'patch'].includes(originalRequest.method?.toLowerCase() ?? '')
        ) {
          originalRequest._csrf401Retry = true

          if (process.env.NODE_ENV === 'development') {
            console.log('🔒 401 error on mutation request, refreshing CSRF token and retrying...')
          }

          await this.refreshCsrfToken()

          // Remove the _csrfRetry flag so the request interceptor will update the CSRF token header
          delete originalRequest._csrfRetry

          return this.axios(originalRequest)
        }

        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !isAuthRequest && !originalRequest._retry) {
          originalRequest._retry = true

          if (this.isRefreshing) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 Refresh already in progress, waiting...')
            }
            try {
              await this.refreshPromise
              return this.axios(originalRequest)
            } catch (refreshError) {
              this.handleAuthFailure()
              return Promise.reject(refreshError)
            }
          }

          this.isRefreshing = true
          this.refreshPromise = this.refreshAuthToken()
            .then(() => {
              this.isRefreshing = false
              this.refreshPromise = null
            })
            .catch((err) => {
              this.isRefreshing = false
              this.refreshPromise = null
              throw err
            })

          try {
            await this.refreshPromise
            return this.axios(originalRequest)
          } catch (refreshError) {
            this.handleAuthFailure()
            return Promise.reject(refreshError)
          }
        }

        if (
          error.response?.status === HTTP_STATUS.FORBIDDEN &&
          !originalRequest._csrfRetry &&
          ['post', 'put', 'delete', 'patch'].includes(originalRequest.method?.toLowerCase() ?? '')
        ) {
          originalRequest._csrfRetry = true

          if (process.env.NODE_ENV === 'development') {
            console.log('🔒 403 error, refreshing CSRF token and retrying...')
          }

          await this.refreshCsrfToken()
          return this.axios(originalRequest)
        }

        if (
          !error.response &&
          originalRequest &&
          !originalRequest._retryCount &&
          this.isIdempotentMethod(originalRequest.method)
        ) {
          return this.retryRequest(originalRequest, error)
        }

        try {
          analyticsService.track('system.api.error', {
            method: originalRequest.method,
            url: originalRequest.url,
            status_code: error.response?.status,
            error_message: error.message,
          })
        } catch {
          // Analytics must never break the app
        }

        this.handleApiError(error)

        return Promise.reject(error)
      }
    )
  }

  private setupOfflineDetection(): void {
    window.addEventListener('online', () => {
      this.isOnline = true
      toast.success('Connection restored')
      this.processOfflineQueue()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
      toast.error('No internet connection')
    })
  }

  // Retrying/replaying a non-idempotent request (POST/PATCH) that may have already reached the
  // server before the network dropped produces duplicate rows in prod (issue #1453). Only GET/
  // HEAD/OPTIONS (safe) and PUT/DELETE (idempotent) are safe to auto-resend.
  private isIdempotentMethod(method?: string): boolean {
    return ['get', 'head', 'options', 'put', 'delete'].includes(method?.toLowerCase() ?? '')
  }

  private async retryRequest(config: CustomAxiosRequestConfig, error: AxiosError): Promise<AxiosResponse> {
    config._retryCount = config._retryCount || 0

    if (config._retryCount >= this.retryCount) {
      return Promise.reject(error)
    }

    config._retryCount++

    const backoffDelay = Math.min(1000 * Math.pow(2, config._retryCount), 10000)

    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 Retrying request (${config._retryCount}/${this.retryCount}) after ${backoffDelay}ms`)
    }

    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const response = await this.axios(config)
          resolve(response)
        } catch (retryError) {
          if (config._retryCount >= this.retryCount) {
            reject(retryError)
          } else {
            resolve(this.retryRequest(config, retryError as AxiosError))
          }
        }
      }, backoffDelay)
    })
  }

  private async refreshAuthToken(): Promise<AxiosResponse> {
    return axios.post(`${getApiBaseUrl()}/auth/refresh`, {}, { withCredentials: true })
  }

  private handleAuthFailure(): void {
    const publicPaths = [
      '/',
      '/login',
      '/signup',
      '/verify-email-pending',
      '/forgot-password',
      '/reset-password',
      '/auth/callback',
      '/auth/error'
    ]
    const currentPath = window.location.pathname

    const isPublicPage = publicPaths.some((path) => currentPath === path || currentPath.startsWith(path))

    if (!isPublicPage) {
      window.location.href = '/'
      toast.error('Session expired. Please login again.')
    }
  }

  private handleApiError(error: AxiosError): void {
    const config = error.config as CustomAxiosRequestConfig | undefined
    if (config?.suppressErrorToast) {
      return
    }

    if (error.response) {
      const { status, data } = error.response

      const publicPaths = [
        '/',
        '/login',
        '/signup',
        '/verify-email-pending',
        '/forgot-password',
        '/reset-password',
        '/auth/callback',
        '/auth/error'
      ]
      const currentPath = window.location.pathname
      const isPublicPage = publicPaths.some((path) => currentPath === path || currentPath.startsWith(path))

      if (status === HTTP_STATUS.UNAUTHORIZED && isPublicPage) {
        return
      }

      if (status === 429 && isPublicPage) {
        return
      }

      const responseData = data as Record<string, unknown> | undefined

      switch (status) {
        case HTTP_STATUS.BAD_REQUEST:
          toast.error((responseData?.message as string) || 'Invalid request')
          break
        case HTTP_STATUS.FORBIDDEN:
          toast.error('You do not have permission to perform this action')
          break
        case HTTP_STATUS.NOT_FOUND:
          toast.error('Resource not found')
          break
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          toast.error('Server error. Please try again later.')
          break
        default:
          if (status === 409 && responseData?.code === 'CAPACITY_FULL') {
            break
          }
          if (status >= 400 && status < 500) {
            toast.error((responseData?.message as string) || 'Request failed')
          } else if (status >= 500) {
            toast.error('Server error. Please try again later.')
          }
      }
    } else if (error.request) {
      if (!this.isOnline) {
        this.queueOfflineRequest(error.config as unknown as OfflineRequest)
      } else {
        toast.error('Network error. Please check your connection.')
      }
    }
  }

  private queueOfflineRequest(config: OfflineRequest): void {
    // Only replay idempotent mutations; a queued POST/PATCH would re-run on reconnect and
    // duplicate a request that may have already reached the server (issue #1453).
    const method = config.method?.toLowerCase()
    if (method !== 'put' && method !== 'delete') {
      return
    }

    // retryRequest re-enters this handler on every failed attempt while offline, so an
    // unguarded push would queue (and toast) the same request several times and replay it
    // repeatedly on reconnect. Skip if an identical request (method + url + body) is already
    // queued (issue #1453).
    const isAlreadyQueued = this.offlineQueue.some(
      (queued) =>
        queued.method?.toLowerCase() === method &&
        queued.url === config.url &&
        JSON.stringify(queued.data) === JSON.stringify(config.data)
    )
    if (isAlreadyQueued) {
      return
    }

    this.offlineQueue.push(config)
    toast('Request queued. Will retry when connection is restored.')
  }

  private async processOfflineQueue(): Promise<void> {
    while (this.offlineQueue.length > 0) {
      const config = this.offlineQueue.shift()
      if (!config) continue

      try {
        await this.axios(config)
        toast.success('Queued request completed')
      } catch (error) {
        console.error('Failed to process queued request:', error)
        toast.error('Failed to process queued request')
      }
    }
  }

  async ensureCsrfToken(): Promise<void> {
    if (this.csrfInitialized) {
      await this.csrfInitialized
    }
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axios.get<T>(url, config)
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    await this.ensureCsrfToken()
    return this.axios.post<T>(url, data, config)
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    await this.ensureCsrfToken()
    return this.axios.put<T>(url, data, config)
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    await this.ensureCsrfToken()
    return this.axios.patch<T>(url, data, config)
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    await this.ensureCsrfToken()
    return this.axios.delete<T>(url, config)
  }

  setAuthToken(_token: string): void {
    console.warn('setAuthToken is deprecated - auth tokens are now in HttpOnly cookies')
  }

  clearAuth(): void {
    // Cookies are cleared by the backend on logout
  }

  getCancelToken() {
    return axios.CancelToken.source()
  }

  isCancel(error: unknown): error is AxiosError {
    return axios.isCancel(error)
  }

  private async initializeCsrf(): Promise<void> {
    try {
      await this.axios.get('/csrf')
      if (process.env.NODE_ENV === 'development') {
        console.log('🔒 CSRF token initialized')
      }
    } catch (error) {
      console.error('Failed to initialize CSRF token:', error)
    }
  }

  private async refreshCsrfToken(): Promise<void> {
    await this.initializeCsrf()
  }
}

export const apiClient = new ApiClient()
